import { useMutation, useQueryClient } from '@tanstack/react-query'
import { exitSelecting } from '@/lib/library-selection-store'
import { trackRequest } from '@/realtime/file-job-monitor'
import { fileJobApi, type ZipJobCreated, zipJobListKey } from '@/services/file-job.service'

/** What one confirmation submits: the complete selection, plus the key that makes a retry a replay. */
export interface CreateZipJobInput {
	fileIds: string[]
	idempotencyKey: string
}

/**
 * Submit one ZIP request.
 *
 * Everything here is an effect of ACCEPTANCE and lives in `onSuccess` alone —
 * deliberately not `onSettled`, which the general create-mutation shape would
 * use: a rejected creation must leave File Job state completely untouched, and
 * `onSettled` runs on a rejection too. There is nothing to patch optimistically
 * (the creation response carries no listing row), so the listing is invalidated
 * rather than seeded — a hand-built row would be a second, frontend-authored
 * definition of what a row looks like.
 *
 * `onError` deliberately does nothing at all: the confirmation renders the
 * failure in place, and raising a notification for it too would report one
 * rejected request on two surfaces.
 */
export function useCreateZipJob() {
	const queryClient = useQueryClient()
	return useMutation<ZipJobCreated, unknown, CreateZipJobInput>({
		mutationFn: ({ fileIds, idempotencyKey }) => fileJobApi.createZipJob(fileIds, idempotencyKey),
		onSuccess: (created) => {
			exitSelecting()
			// A request the backend reports as already terminal (reuse of complete
			// work) has nothing left to follow; `trackRequest` ignores it.
			trackRequest(created)
			void queryClient.invalidateQueries({ queryKey: zipJobListKey() })
		},
	})
}
